// Rebuy cart drawer: visually group line items that share a "_bundle_group" cart property
// into a single collapsible row with its own image (see sections/prebuilt-bundles.liquid for
// how the property + localStorage bundle info are set).
//
// Loaded globally (theme.liquid) rather than only alongside the prebuilt-bundles section:
// the cart drawer can be opened from ANY page, not just the one the shopper added the bundle
// from, so this has to run everywhere to keep the grouping consistent. It's a no-op on pages
// with no bundle line items.
(function () {
  function formatMoney(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function' && window.themeMoneyFormat) {
      return window.Shopify.formatMoney(cents, window.themeMoneyFormat);
    }
    return '$' + (cents / 100).toFixed(2);
  }

  function getBundleInfo(groupId) {
    try {
      const raw = localStorage.getItem('tgthr_bundle_' + groupId);
      if (raw) return JSON.parse(raw);
    } catch (e) {
      // ignore
    }
    return null;
  }

  // Removes every real line item in the bundle, not just one. Re-queries the DOM by groupId
  // before each click (rather than reusing the `groupItems` array captured at render time)
  // because Rebuy re-renders its own item list after every cart change, which can replace
  // those DOM nodes outright — a stale reference's remove button would silently do nothing.
  function removeBundleGroup(root, groupId) {
    const step = () => {
      const items = Array.from(root.querySelectorAll('.rebuy-cart__flyout-item:not(.tgthr-bundle-summary)')).filter(
        (item) => Array.from(item.classList).indexOf('property-_bundle_group:' + groupId) !== -1
      );
      if (!items.length) return;
      const removeBtn = items[0].querySelector('.rebuy-cart__flyout-item-remove');
      if (!removeBtn) return;
      removeBtn.click();
      setTimeout(step, 700); // give Rebuy's own AJAX update + re-render time to finish first
    };
    step();
  }

  function enhanceRebuyCart(root, observer) {
    // enhanceRebuyCart mutates `root`'s own subtree (inserting/updating the summary row,
    // toggling hidden classes), and it's called BY a MutationObserver watching that same
    // subtree — without disconnecting first, every run queues a new mutation record that
    // re-triggers the observer, which runs this again, forever. That infinite loop pegs the
    // tab's JS thread and freezes the whole page the instant the cart drawer opens.
    if (observer) observer.disconnect();

    const items = Array.from(root.querySelectorAll('.rebuy-cart__flyout-item:not(.tgthr-bundle-summary)'));
    const groups = new Map();

    items.forEach((item) => {
      const token = Array.from(item.classList).find((cls) => cls.indexOf('property-_bundle_group:') === 0);
      if (!token) return;
      const groupId = token.slice('property-_bundle_group:'.length);
      if (!groupId) return;
      if (!groups.has(groupId)) groups.set(groupId, []);
      groups.get(groupId).push(item);
    });

    root.querySelectorAll('[data-bundle-summary]').forEach((summary) => {
      const groupId = summary.dataset.bundleSummary;
      const groupItems = groups.get(groupId);
      if (!groupItems || groupItems.length < 2) {
        summary.remove();
        if (groupItems) groupItems.forEach((item) => item.classList.remove('tgthr-bundle-item-hidden'));
      }
    });

    groups.forEach((groupItems, groupId) => {
      if (groupItems.length < 2) return;

      const info = getBundleInfo(groupId);
      const bundleName = (info && info.name) || 'Bundle';
      const bundleImage = info && info.image;
      const totalPrice = groupItems.reduce((sum, item) => {
        // Rebuy's price markup includes both the sale price AND the crossed-out compare-at
        // price in the same element ("Sale price $60.00 Original price $75.00") — grabbing
        // its whole textContent and stripping non-digits ran the two numbers together
        // ("60.0075.00"), inflating the total by a cent or more per item. `.rebuy-money.sale`
        // isolates just the actual line price.
        const priceEl = item.querySelector('.rebuy-money.sale') || item.querySelector('[class*="price" i]');
        const priceText = priceEl?.textContent || '';
        const match = priceText.match(/\d+(?:[.,]\d+)?/);
        const value = match ? parseFloat(match[0].replace(',', '.')) : NaN;
        return sum + (Number.isFinite(value) ? Math.round(value * 100) : 0);
      }, 0);

      let summary = root.querySelector(`[data-bundle-summary="${groupId}"]`);
      if (!summary) {
        summary = document.createElement('div');
        summary.className = 'rebuy-cart__flyout-item tgthr-bundle-summary';
        summary.dataset.bundleSummary = groupId;
        summary.dataset.expanded = 'false';
        groupItems[0].insertAdjacentElement('beforebegin', summary);
      }

      const expanded = summary.dataset.expanded === 'true';

      summary.innerHTML =
        '<div class="tgthr-bundle-summary__row">' +
        (bundleImage
          ? '<div class="tgthr-bundle-summary__image"><img src="' + bundleImage + '" alt="" loading="lazy"></div>'
          : '') +
        '<div class="tgthr-bundle-summary__text">' +
        '<span class="tgthr-bundle-summary__name">' + bundleName + '</span>' +
        '<span class="tgthr-bundle-summary__price">' + formatMoney(totalPrice) + '</span>' +
        '</div>' +
        '<div class="tgthr-bundle-summary__actions">' +
        '<button type="button" class="tgthr-bundle-summary__remove" aria-label="Remove ' +
        bundleName +
        '"><i class="far fa-trash" aria-hidden="true"></i></button>' +
        '<button type="button" class="tgthr-bundle-summary__toggle">' +
        (expanded ? 'Hide' : 'Show') + ' ' + groupItems.length + ' items' +
        '</button>' +
        '</div>' +
        '</div>';

      summary.querySelector('.tgthr-bundle-summary__toggle').addEventListener('click', function () {
        summary.dataset.expanded = expanded ? 'false' : 'true';
        enhanceRebuyCart(root, observer);
      });

      summary.querySelector('.tgthr-bundle-summary__remove').addEventListener('click', function () {
        removeBundleGroup(root, groupId);
      });

      groupItems.forEach((item) => {
        item.classList.toggle('tgthr-bundle-item-hidden', !expanded);
      });
    });

    if (observer) observer.observe(root, { childList: true, subtree: true });
  }

  function observeCart(cart) {
    if (cart.dataset.tgthrBundleObserved) return;
    cart.dataset.tgthrBundleObserved = 'true';
    const observer = new MutationObserver(() => enhanceRebuyCart(cart, observer));
    enhanceRebuyCart(cart, observer);
  }

  function waitForCart(attemptsLeft) {
    const cart = document.getElementById('rebuy-cart');
    if (cart) {
      observeCart(cart);
      return;
    }
    if (attemptsLeft <= 0) return;
    setTimeout(() => waitForCart(attemptsLeft - 1), 500);
  }

  function init() {
    waitForCart(40); // ~20s of polling, covers slow Rebuy script loads
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
